/**
 * ui/BackupManager — Sauvegardes d'un site, depuis la console d'administration
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── UNE SEULE IMPLÉMENTATION, DEUX SURFACES ─────────────────────────────────
 * Ce composant vit dans `AdminShell`. Or `AdminShell` est monté DEUX fois :
 *   - dans `admin.exe`, en LAN, avec `baseUrl` = l'URL du serveur du site ;
 *   - dans la console centrale, avec `baseUrl` = `/api/sites/<id>/proxy` (relais WebSocket).
 *
 * Le même code pilote donc les sauvegardes d'un serveur voisin ET celles d'un cabinet distant, sans
 * une ligne de différence. C'est tout l'intérêt du relais : il n'y a pas deux API à maintenir, et
 * donc pas deux comportements qui divergent silencieusement.
 *
 * ─── LE BOUTON QUI COMPTE N'EST PAS « SAUVEGARDER » ──────────────────────────
 * C'est « Tester la restauration ». Une sauvegarde n'a de valeur que RESTAURÉE : on peut accumuler
 * deux ans d'archives affichées en vert et découvrir, le jour du sinistre, qu'elles ne se
 * rechargent pas. L'essai rejoue l'archive dans une base JETABLE et RECOMPTE. La vérification
 * d'intégrité, elle, dit seulement que les octets sont intacts — pas qu'ils se rechargent.
 */

import React, { useCallback, useEffect, useState } from 'react';

const octets = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} Mo` : `${Math.round(n / 1024)} Ko`);
const quand = (s) => new Date(s).toLocaleString('fr-FR');

export default function BackupManager({ api }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');

  const charger = useCallback(async () => {
    try {
      const d = await api.getBackup();
      setData(d);
      setCfg(d.config);
    } catch (e) {
      setMsg({ ko: e.response?.data?.error || e.message });
    }
  }, [api]);

  useEffect(() => { charger(); }, [charger]);

  const action = async (cle, fn) => {
    setBusy(cle); setMsg(null);
    try { await fn(); } catch (e) {
      setMsg({ ko: e.response?.data?.error || e.message });
    } finally { setBusy(''); }
  };

  const enregistrer = () => action('cfg', async () => {
    setCfg(await api.saveBackup(cfg));
    setMsg({ ok: 'Réglages enregistrés — la planification du site est relancée immédiatement.' });
    await charger();
  });

  const sauvegarder = () => action('run', async () => {
    const b = await api.runBackup();
    setMsg({ ok: `Sauvegarde créée : ${b.path} (${octets(b.size)})` });
    await charger();
  });

  const verifier = (p) => action(`v:${p}`, async () => {
    const v = await api.verifyBackup(p);
    setMsg(v.integrity.ok
      ? { ok: `Intégrité OK — ${Object.entries(v.counts).map(([k, n]) => `${k}: ${n}`).join(', ')}` }
      : { ko: `Archive CORROMPUE (${v.integrity.reason})` });
  });

  const essayer = (p) => action(`t:${p}`, async () => {
    const t = await api.testRestoreBackup(p);
    const total = Object.values(t.obtenus || {}).reduce((a, b) => a + b, 0);
    setMsg(t.verifie
      ? { ok: `Restauration d’essai RÉUSSIE — ${total} enregistrements relus dans une base jetable. Cette archive est utilisable.` }
      : { ko: 'Restauration d’essai ÉCHOUÉE : les compteurs ne coïncident pas. Cette archive n’est PAS fiable.' });
  });

  /**
   * Restauration RÉELLE d'un site DISTANT. C'est l'opération la plus destructrice du produit, et
   * elle est ici déclenchée à distance, sur la base d'un client — la pire combinaison possible.
   *
   * Le `prompt` n'est PAS la protection : le serveur du site exige lui-même `apply` + `confirm`
   * égal au chemin exact, et refuse tout seul si on l'appelle en `curl`. L'IHM ne fait que rendre
   * l'accident difficile ; c'est le serveur qui le rend impossible.
   */
  const restaurer = (p) => {
    const saisi = window.prompt(
      'RESTAURATION RÉELLE SUR LE SITE — cette opération ÉCRASE les données du client.\n\n'
      + `Pour confirmer, recopiez exactement le nom de l’archive :\n${p}`,
    );
    if (saisi !== p) { setMsg({ ko: 'Restauration annulée (confirmation incorrecte).' }); return; }

    action(`r:${p}`, async () => {
      const t = await api.restoreBackup(p);
      const total = Object.values(t.obtenus || {}).reduce((a, b) => a + b, 0);
      setMsg({ ok: `Restauration effectuée — ${total} enregistrements réinjectés.` });
    });
  };

  if (!cfg) return <p className="muted">…</p>;
  const d = data?.lastRun;

  return (
    <div className="backup-manager">
      <h2>💾 Sauvegardes</h2>

      {msg?.ok && <div className="status">✅ {msg.ok}</div>}
      {msg?.ko && <div className="status error">❌ {msg.ko}</div>}

      <div className="card">
        <h3>Planification</h3>
        <label>
          <input
            type="checkbox" checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          /> Sauvegardes automatiques
        </label>

        <div className="row">
          <select value={cfg.mode} onChange={(e) => setCfg({ ...cfg, mode: e.target.value })}>
            <option value="daily">Chaque jour</option>
            <option value="weekly">Chaque semaine</option>
            <option value="monthly">Chaque mois</option>
          </select>
          <input type="time" value={cfg.time} onChange={(e) => setCfg({ ...cfg, time: e.target.value })} />
          <input
            type="number" min={1} value={cfg.keepLast} style={{ width: 80 }}
            onChange={(e) => setCfg({ ...cfg, keepLast: Number(e.target.value) })}
          />
          <span className="muted">archives conservées</span>
        </div>

        <input
          value={cfg.dossier} placeholder="Dossier de sauvegarde (sur le serveur du site)"
          onChange={(e) => setCfg({ ...cfg, dossier: e.target.value })}
        />

        <p className="muted">
          Chaque sauvegarde automatique est suivie d’une <b>restauration d’essai</b> dans une base
          jetable. Les anciennes archives ne sont supprimées <b>qu’après</b> qu’une nouvelle ait été
          créée <i>et</i> vérifiée — la dernière copie valide n’est jamais détruite.
        </p>

        <div className="row">
          <button onClick={enregistrer} disabled={busy === 'cfg'}>Enregistrer</button>
          <button className="ghost" onClick={sauvegarder} disabled={busy === 'run'}>
            {busy === 'run' ? 'Sauvegarde en cours…' : '💾 Sauvegarder maintenant'}
          </button>
        </div>

        {d && (
          <p className="muted">
            Dernier cycle automatique : <b>{d.ok ? '✅ réussi' : '❌ échoué'}</b> le {quand(d.at)}
            {d.restored != null && ` — ${d.restored} enregistrements relus à l’essai`}
            {d.error && <span className="error"> — {d.error}</span>}
          </p>
        )}
      </div>

      <div className="card">
        <h3>Archives ({data?.backups?.length ?? 0})</h3>
        {!data?.backups?.length ? (
          <p className="muted">Aucune sauvegarde sur ce site.</p>
        ) : (
          <table className="perm-matrix">
            <thead><tr><th>Date</th><th>Taille</th><th /></tr></thead>
            <tbody>
              {data.backups.map((b) => (
                <tr key={b.path}>
                  <td>{quand(b.createdAt)}</td>
                  <td>{octets(b.size)}</td>
                  <td>
                    <button type="button" className="ghost" onClick={() => verifier(b.path)} disabled={busy === `v:${b.path}`}>
                      Vérifier
                    </button>
                    <button type="button" onClick={() => essayer(b.path)} disabled={busy === `t:${b.path}`}>
                      {busy === `t:${b.path}` ? 'Essai…' : '🧪 Tester la restauration'}
                    </button>
                    <button type="button" className="ghost danger" onClick={() => restaurer(b.path)} disabled={busy === `r:${b.path}`}>
                      Restaurer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="muted">
          ⚠️ <b>Restaurer</b> écrase les données du site. Utilisez d’abord
          <b> Tester la restauration</b> : c’est sans risque, et c’est la seule opération qui prouve
          que l’archive est réellement utilisable.
        </p>
      </div>
    </div>
  );
}
